import { type Aperture, type ComponentEffect, toRender } from "refract-rxjs";
import { combineLatest } from "rxjs";
import { filter, map, startWith, tap } from "rxjs/operators";

import { type GetOrganicStatsQuery } from "../api/queries";
import { computeAdditionalMetrics } from "../computers";
import { generateDefaultMetrics } from "../generators";
import { mapInsightsComputedMetrics, mapInsightsMetrics } from "../mapping";
import {
    type AdditionalInsightsData,
    ComputedMetricField,
    type InsightsMetricsMap,
    type LensGranularity,
    type StatsItem,
    metricsFields,
} from "../models";

import { mapMetricsComputed } from "./mapMetricsComputed";
import { mapTotalAndDiffValues } from "./mapTotalAndDiffValues";

export interface LensOrganicStatsInput {
    chartsData: GetOrganicStatsQuery | undefined;
    chartsLoading: boolean;
    lifetimeData: GetOrganicStatsQuery | undefined;
    lifetimeLoading: boolean;
    selectedTimeRange: LensGranularity;
    lensId: string | null;
    setSubscribersTotalValues: (data: GetOrganicStatsQuery) => void;
}

export interface LensOrganicStatsOutput {
    chartsMetrics: InsightsMetricsMap<StatsItem>;
    chartsMetricsComputed: InsightsMetricsMap<StatsItem>;
    additionalInsights: AdditionalInsightsData | Partial<AdditionalInsightsData>;
}

export const lensOrganicStatsEffect: Aperture<LensOrganicStatsInput, ComponentEffect<LensOrganicStatsOutput>> = ({
    observe,
}) => {
    const chartsData$ = observe<GetOrganicStatsQuery | undefined>("chartsData");
    const chartsLoading$ = observe<boolean>("chartsLoading");
    const lifetimeData$ = observe<GetOrganicStatsQuery | undefined>("lifetimeData");
    const lifetimeLoading$ = observe<boolean>("lifetimeLoading");
    const selectedTimeRange$ = observe<LensGranularity>("selectedTimeRange");
    const lensId$ = observe<string>("lensId");
    const setSubscribersTotalValues$ = observe<(data: GetOrganicStatsQuery) => void>("setSubscribersTotalValues");

    const lifetime$ = combineLatest([lifetimeData$, lifetimeLoading$]).pipe(
        filter((values): values is [GetOrganicStatsQuery, boolean] => {
            const [data, loading] = values;
            return !loading && data !== undefined && !!data.getMyLensesOrganicStats;
        }),
        map(([data]) => data.getMyLensesOrganicStats.responseEntryList),
        startWith([]),
    );

    const charts$ = combineLatest([chartsData$, chartsLoading$, setSubscribersTotalValues$]).pipe(
        filter((values): values is [GetOrganicStatsQuery, boolean, (data: GetOrganicStatsQuery) => void] => {
            const [data, loading] = values;
            return !loading && data !== undefined && !!data.getMyLensesOrganicStats;
        }),
        tap(([data, , setSubscribersTotalValues]) => {
            setSubscribersTotalValues(data);
        }),
        map(([data]) => {
            return {
                organic: { ...mapInsightsMetrics(data.getMyLensesOrganicStats.responseEntryList, metricsFields) },
                computed: { ...mapInsightsComputedMetrics(data.getMyLensesOrganicStats.responseEntryList) },
            };
        }),
    );

    return combineLatest([charts$, selectedTimeRange$, lensId$, lifetime$]).pipe(
        map(
            ([
                {
                    organic: { metrics, metricsPrevious },
                    computed,
                },
                selectedTimeRange,
                lensId,
                lifetimeData,
            ]) => {
                return {
                    chartsMetrics: mapTotalAndDiffValues({
                        metrics,
                        metricsPrevious,
                        lifetimeData,
                        selectedTimeRange,
                    }),
                    chartsMetricsComputed: mapMetricsComputed({
                        metrics,
                        metricsPrevious,
                        lifetimeData,
                        selectedTimeRange,
                        computed,
                    }),
                    additionalInsights: lensId ? computeAdditionalMetrics(metrics, metricsPrevious) : {},
                };
            },
        ),
        startWith({
            chartsMetrics: generateDefaultMetrics(metricsFields),
            chartsMetricsComputed: generateDefaultMetrics([
                ComputedMetricField.LensCTR,
                ComputedMetricField.AverageLensFaceFound,
                ComputedMetricField.AverageLensFaceLost,
                ComputedMetricField.AverageLensObjectTracked,
                ComputedMetricField.AverageLensObjectTracked_3D,
                ComputedMetricField.AverageLensMarkerTracked,
                ComputedMetricField.AverageLensTap,
            ]),
            additionalInsights: {},
        }),
        map(({ chartsMetrics, chartsMetricsComputed, additionalInsights }) =>
            toRender<LensOrganicStatsOutput>({
                chartsMetrics,
                chartsMetricsComputed,
                additionalInsights,
            }),
        ),
    );
};
